import {
  Box,
  Flex,
  HStack,
  IconButton,
  Link,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { RiPercentLine } from "@remixicon/react";
import { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { DeleteIconButton } from "@/common/components/CommonIconButtons";
import { FilterGroup } from "@/common/components/filters/FilterGroup";
import FormNumberInput from "@/common/components/Form/FormNumberInput";
import FormSwitchButtonGroup from "@/common/components/Form/FormSwitchButtonGroup";
import { ManagedFormControl } from "@/common/components/Form/ManagedFormControl";
import { SegmentStatelessRestrictionsWarning } from "@/common/components/Rule/RuleRestrictions";
import {
  AttributeField,
  FIRST_SEEN_FIELD_KEY,
  LAST_SEEN_FIELD_KEY,
  USER_COUNT_FIELD_KEY,
  USER_ID_FIELD_KEY,
  USER_ID_SYSTEM_ATTR,
} from "@reflag/shared/attributeFilter";
import { CustomRule } from "@reflag/shared/featureTargetingAPI";
import {
  COMPANY_ID_CONTEXT_FIELD,
  MAX_ROLLOUT_THRESHOLD,
  normalizeRolloutContextAttribute,
  SupportedFlagFilterTypes,
  UIFilterType,
  USER_ID_CONTEXT_FIELD,
  wrapWithFilterGroup,
} from "@reflag/shared/filter";

export function adjustFlagAttributeFields(
  filterType: UIFilterType,
  fields: AttributeField[],
) {
  if (filterType === "userAttribute") {
    fields = [
      USER_ID_SYSTEM_ATTR,
      ...fields.filter(
        (f) =>
          f.key !== "id" &&
          f.key !== USER_ID_FIELD_KEY &&
          f.key !== LAST_SEEN_FIELD_KEY &&
          f.key !== FIRST_SEEN_FIELD_KEY,
      ),
    ];
  } else if (filterType === "companyAttribute") {
    fields = fields.filter(
      (f) =>
        f.key !== LAST_SEEN_FIELD_KEY &&
        f.key !== FIRST_SEEN_FIELD_KEY &&
        f.key !== USER_COUNT_FIELD_KEY,
    );
  }

  return fields;
}

type RuleAdvancedFormProps = {
  baseName: string;
  remove?: () => void;
};

export function RuleAdvancedForm({
  baseName = "customRules",
  remove,
}: RuleAdvancedFormProps) {
  const { setValue, trigger } = useFormContext();
  const rollout = useWatch({
    name: `${baseName}.partialRolloutThreshold`,
  });
  const rolloutContextAttribute = useWatch({
    name: `${baseName}.partialRolloutContextAttribute`,
  });
  const normalizedRolloutContextAttribute = normalizeRolloutContextAttribute(
    rolloutContextAttribute,
  );
  const [showRollout, setShowRollout] = useState(
    rollout < MAX_ROLLOUT_THRESHOLD,
  );

  useEffect(() => {
    if (rolloutContextAttribute !== normalizedRolloutContextAttribute) {
      setValue(
        `${baseName}.partialRolloutContextAttribute`,
        normalizedRolloutContextAttribute,
        { shouldDirty: false },
      );
    }
  }, [
    baseName,
    normalizedRolloutContextAttribute,
    rolloutContextAttribute,
    setValue,
  ]);

  return (
    <Box as="fieldset">
      <Flex>
        <ManagedFormControl
          _inputGroup={{
            alignItems: "center",
            flexDirection: "row",
            flexWrap: "wrap",
          }}
          name={`${baseName}.filter`}
          render={({ field: { value, onChange } }) => (
            <FilterGroup
              adjustAttributeFields={adjustFlagAttributeFields}
              context="flag"
              segmentFilterFn={(s) =>
                s.stateless ? "supported" : "unsupported"
              }
              segmentUnsupportedReason={<SegmentStatelessRestrictionsWarning />}
              showItemsIcons={true}
              types={SupportedFlagFilterTypes}
              value={wrapWithFilterGroup(value)}
              flatten
              onChange={onChange}
            />
          )}
        />
        <Tooltip
          label={
            showRollout
              ? "Disable percentage rollout"
              : "Enable percentage rollout"
          }
          hasArrow
        >
          <IconButton
            aria-label="Gradual rollout"
            color={!showRollout ? "dimmed" : undefined}
            colorScheme={showRollout ? "gray" : undefined}
            icon={<RiPercentLine size={18} />}
            mr={0.5}
            mt={0.5}
            size="xs"
            variant={showRollout ? "subtle" : "ghost"}
            onClick={() => {
              setShowRollout(!showRollout);
              if (showRollout) {
                setValue(
                  `${baseName}.partialRolloutThreshold`,
                  MAX_ROLLOUT_THRESHOLD,
                  {
                    shouldDirty: true,
                  },
                );
                trigger(baseName);
              }
            }}
          />
        </Tooltip>
        <DeleteIconButton
          color="dimmed"
          label="Remove"
          mt={0.5}
          onClick={() => remove?.()}
        />
      </Flex>

      {showRollout && (
        <Flex
          alignItems="center"
          borderTopColor="appBorder"
          borderTopWidth={1}
          gap={4}
          mt={3}
          pt={3}
        >
          <FormNumberInput
            _control={{ w: 24 }}
            _label={{ fontWeight: "medium" }}
            fontSize="sm"
            max={100}
            min={0}
            name={`${baseName}.partialRolloutThreshold`}
            scaleFactor={100 / MAX_ROLLOUT_THRESHOLD} // 0-100% = 0-100000
            unit="%"
          />
          <HStack color="dimmed" fontSize="sm" spacing={1}>
            <Text>roll-out to a part of</Text>
            <FormSwitchButtonGroup
              _control={{ flex: "0 0 auto", w: "auto" }}
              buttons={[
                { id: COMPANY_ID_CONTEXT_FIELD, label: "companies" },
                { id: USER_ID_CONTEXT_FIELD, label: "users" },
              ]}
              name={`${baseName}.partialRolloutContextAttribute`}
              size="xs"
            />
            <Text>matched by the filters.</Text>
            <Link
              href="https://docs.reflag.com/product-handbook/feature-rollouts/feature-targeting-rules#specify-rollout-percentage"
              target="_blank"
            >
              Learn more
            </Link>
          </HStack>
        </Flex>
      )}
    </Box>
  );
}

type RuleAdvancedDisplayProps = {
  targetingRule: CustomRule;
};

export const RuleAdvancedDisplay = ({
  targetingRule,
}: RuleAdvancedDisplayProps) => {
  const { filter, partialRolloutThreshold, partialRolloutContextAttribute } =
    targetingRule;
  const rolloutEntity =
    partialRolloutContextAttribute === USER_ID_CONTEXT_FIELD
      ? "users"
      : "companies";
  return (
    <Box>
      <FilterGroup
        adjustAttributeFields={adjustFlagAttributeFields}
        context="flag"
        types={SupportedFlagFilterTypes}
        value={wrapWithFilterGroup(filter)}
        isReadOnly
      />
      {typeof partialRolloutThreshold === "number" &&
        partialRolloutThreshold < MAX_ROLLOUT_THRESHOLD && (
          <Flex
            alignItems="center"
            borderTopColor="appBorder"
            borderTopWidth={1}
            gap={4}
            mt={3}
            pt={3}
          >
            <Text fontSize="sm" fontWeight="medium">
              {Math.round(
                (partialRolloutThreshold / MAX_ROLLOUT_THRESHOLD) * 100,
              )}
              %
            </Text>
            <Text color="dimmed" fontSize="sm">
              Roll-out to a part of {rolloutEntity} matched by the filters
            </Text>
          </Flex>
        )}
    </Box>
  );
};
